import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StatCard from "./StatCard";
import ReviewForm from "./ReviewForm";
import { Stars } from "./Stars";
import { openChat } from "./chatActions";
import { apiRequest, errorMessage } from "../../api";
import { cardClass, timeAgo, STATUS_LABELS, STATUS_STYLES } from "./jobUtils";

export default function SellerDashboard({ settings }) {
  const currency = settings?.currency_label || "USD";
  const [stats, setStats] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [error, setError] = useState("");
  const [reviewing, setReviewing] = useState(null);
  const navigate = useNavigate();

  const chat = async (buyerId) => {
    setError("");
    const message = await openChat(buyerId, navigate);
    if (message) setError(message);
  };

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      apiRequest("/core/cowork/dashboard/", { auth: true }),
      apiRequest("/core/cowork/proposals/mine/", { auth: true }),
    ]);
    if (s.ok) setStats(s.data);
    setProposals(p.ok ? p.data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const withdraw = async (proposal) => {
    if (!window.confirm("Withdraw this proposal?")) return;
    setError("");
    const { ok, data } = await apiRequest(`/core/cowork/proposals/${proposal.id}/withdraw/`, { method: "POST", auth: true });
    if (!ok) return setError(errorMessage(data));
    load();
  };

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Proposals sent" value={stats?.proposals_total} />
        <StatCard label="Pending" value={stats?.proposals_pending} />
        <StatCard label="Accepted" value={stats?.proposals_accepted} highlight />
        <StatCard label="Rejected" value={stats?.proposals_rejected} />
        <StatCard label="Open jobs to browse" value={stats?.open_jobs} />
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold">My Proposals</h2>
        <Link to="/co-work#jobs" className="px-5 py-2 rounded-full bg-brand-gradient font-semibold">Browse Jobs</Link>
      </div>

      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {proposals === null ? (
        <p className="text-gray-500">Loading...</p>
      ) : proposals.length === 0 ? (
        <div className={`${cardClass} text-center py-12`}>
          <p className="font-semibold">You haven't applied to any jobs yet</p>
          <p className="text-gray-400 text-sm mt-1">Browse open jobs and send your first proposal.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {proposals.map((p) => (
            <div key={p.id} className={`${cardClass} p-5`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Link to={`/co-work/jobs/${p.job}`} className="text-lg font-semibold hover:text-accent-light">{p.job_title}</Link>
                  <p className="text-xs text-gray-400 mt-1">Applied {timeAgo(p.created_at)} · Job is {STATUS_LABELS[p.job_status].toLowerCase()}</p>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[p.status]}`}>{STATUS_LABELS[p.status]}</span>
                  <p className="text-lg font-bold text-accent-light mt-2">{currency} {p.bid_amount}</p>
                  <p className="text-xs text-gray-400">{p.delivery_days} day{p.delivery_days === 1 ? "" : "s"}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 mt-3 whitespace-pre-line line-clamp-3">{p.cover_letter}</p>

              {p.buyer_contact && (
                <div className="mt-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm">
                  <p className="font-semibold text-emerald-300">You're hired! Contact the buyer to get started:</p>
                  <p className="text-gray-200 mt-1">
                    {p.buyer_contact.name} · <a href={`mailto:${p.buyer_contact.email}`} className="text-accent-light">{p.buyer_contact.email}</a>
                    {p.buyer_contact.phone && <> · {p.buyer_contact.phone}</>}
                  </p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {p.status !== "withdrawn" && (
                  <button onClick={() => chat(p.buyer_id)} className="px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-200 hover:border-accent-light">
                    Message buyer
                  </button>
                )}
                {p.can_review && (
                  <button onClick={() => setReviewing(reviewing === p.id ? null : p.id)} className="px-4 py-1.5 rounded-full bg-brand-gradient text-sm font-semibold">Rate the buyer</button>
                )}
                {p.my_review && (
                  <span className="text-sm text-gray-400 inline-flex items-center gap-1.5">You rated <Stars value={p.my_review.rating} size={12} /></span>
                )}
                {p.status === "pending" && (
                  <button onClick={() => withdraw(p)} className="px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-300 hover:text-white">
                    Withdraw
                  </button>
                )}
              </div>
              {reviewing === p.id && (
                <ReviewForm jobId={p.job} who={p.buyer_contact?.name || "the buyer"} onDone={() => { setReviewing(null); load(); }} onCancel={() => setReviewing(null)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
